import { useEffect, useState } from 'react';
import type { UserRole, LedgerEntryRow } from '@aslgo/shared';
import { getMyLedger, LEDGER_WINDOW_LIMIT } from '../api/client.js';
import { CloseIcon, AlertIcon } from '../icons.js';
import { useAppStore } from '../store/useAppStore.js';

interface Props {
  open: boolean;
  role: UserRole;
  onClose: () => void;
}

export function FinancialDrawer({ open, role, onClose }: Props) {
  if (!open) return null;
  return (
    <>
      <div
        data-testid="financial-drawer-backdrop"
        className="fixed inset-0 bg-ink-900/40 z-40 animate-fade-in"
        onClick={onClose}
      />
      <aside
        role="dialog"
        aria-label={role === 'guide' ? 'Your earnings' : 'Your wallet'}
        className="fixed top-0 right-0 bottom-0 z-50 w-full sm:w-[480px]
                   bg-canvas-light dark:bg-canvas-dark
                   shadow-chrome border-l border-ink-200/60 dark:border-white/[0.06]
                   flex flex-col"
      >
        <header className="flex items-start justify-between gap-4 px-6 py-5 border-b border-ink-200/60 dark:border-white/[0.06] sticky top-0 bg-inherit">
          <div>
            <div className="text-eyebrow mb-1">Account</div>
            <h2 className="h-card text-ink-900 dark:text-ink-100">
              {role === 'guide' ? 'Your earnings' : 'Your wallet'}
            </h2>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="p-2 rounded-lg text-ink-500 dark:text-ink-400 hover:text-ink-900 dark:hover:text-ink-100 hover:bg-ink-900/5 dark:hover:bg-white/5 transition"
          >
            <CloseIcon />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-6 py-6">
          <FinancialBody role={role} />
        </div>
      </aside>
    </>
  );
}

function previousMonthStart(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() - 1, 1, 0, 0, 0, 0);
}

/** Formats a signed dollar amount with the sign OUTSIDE the currency symbol:
 *  "-$7.75", never "$-7.75". Reachable, not theoretical — admin_reverse_entry
 *  and admin_reverse_session write a reversal's dollars as the negation of the
 *  earning, so a guide whose call is reversed has genuinely negative dollars,
 *  and the month hero deliberately sums those reversals in. Rounds to cents
 *  first so an amount that rounds to zero from below reads "$0.00", not
 *  "-$0.00". */
function money(n: number): string {
  const cents = Math.round(n * 100);
  return `${cents < 0 ? '-' : ''}$${(Math.abs(cents) / 100).toFixed(2)}`;
}

function FinancialBody({ role }: { role: UserRole }) {
  const [entries, setEntries] = useState<LedgerEntryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [oldestLoadedStart, setOldestLoadedStart] = useState<Date>(
    () => new Date(new Date().getFullYear(), new Date().getMonth(), 1, 0, 0, 0, 0),
  );
  const [exhausted, setExhausted] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const rows = await getMyLedger(oldestLoadedStart.toISOString(), new Date().toISOString());
        if (!active) return;
        setEntries(rows);
        setTruncated(rows.length >= LEDGER_WINDOW_LIMIT);
      } catch (e) {
        if (active) setError((e as Error).message);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
    // Intentional: runs once on mount. oldestLoadedStart only changes via
    // loadOlder(), which manages its own fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  async function loadOlder() {
    const prevStart = previousMonthStart(oldestLoadedStart);
    setLoadingOlder(true);
    try {
      const older = await getMyLedger(prevStart.toISOString(), oldestLoadedStart.toISOString());
      if (older.length === 0) {
        setExhausted(true);
      } else {
        setEntries((prev) => [...prev, ...older]);
        setOldestLoadedStart(prevStart);
        if (older.length >= LEDGER_WINDOW_LIMIT) setTruncated(true);
      }
    } finally {
      setLoadingOlder(false);
    }
  }

  if (error) {
    return (
      <div className="notice-error">
        <AlertIcon className="shrink-0 mt-0.5" />
        <span>{error}</span>
      </div>
    );
  }
  if (loading) return <p className="hint">Loading…</p>;

  // Headline totals cover the current month only, so expanding the range with
  // "View older" does not inflate them.
  const mtdSince = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString();
  const mtd = entries.filter((e) => e.occurredAt >= mtdSince);
  const calls = mtd.filter((e) => e.kind === 'call');
  // A learner's "minutes used" sums the signed minutesDelta rather than
  // sessionMinutes: a consume whose LiveKit session was never recorded (an
  // orphaned call) carries sessionMinutes: 0 even though minutesDelta is
  // non-zero, so summing sessionMinutes would silently undercount. Guides
  // never have orphans -- an earning row is only ever written once the
  // session resolved -- so sessionMinutes stays correct for them.
  const learnerMinutesUsed = calls.reduce((s, e) => s - e.minutesDelta, 0);
  const guideMinutes = calls.reduce((s, e) => s + (e.sessionMinutes ?? 0), 0);
  const topUps = mtd.filter((e) => e.kind === 'charge');
  const topUpDollars = topUps.reduce((s, e) => s + (e.dollars ?? 0), 0);
  // A guide's period earnings include their reversals, which carry negative
  // dollars — so a reversed call correctly reduces the total.
  const guideDollars = mtd
    .filter((e) => e.kind === 'call' || e.kind === 'reversal')
    .reduce((s, e) => s + (e.dollars ?? 0), 0);

  return (
    <div className="space-y-6">
      {role === 'learner' ? (
        <>
          <LearnerBalanceHero />
          <LearnerHeadline minutesUsed={learnerMinutesUsed} topUpsDollars={topUpDollars} topUpCount={topUps.length} />
        </>
      ) : (
        <GuideHeadline dollars={guideDollars} minutes={guideMinutes} />
      )}

      {entries.length === 0 ? (
        <p className="hint">
          {role === 'guide'
            ? 'No sessions yet this month. Stay online — learners will call.'
            : 'No activity yet this month.'}
        </p>
      ) : (
        <section>
          <div className="text-eyebrow mb-3">{role === 'guide' ? 'Sessions' : 'Activity'}</div>
          <ul className="divide-y divide-ink-200/70 dark:divide-white/[0.06] list-none p-0">
            {entries.map((e) => <EntryRow key={e.entryKey} entry={e} role={role} />)}
          </ul>
          {truncated ? (
            <p className="hint mt-3">Showing the most recent {LEDGER_WINDOW_LIMIT} entries for this period.</p>
          ) : null}
          {!exhausted ? (
            <button
              type="button"
              onClick={() => { void loadOlder(); }}
              disabled={loadingOlder}
              className="btn-ghost w-full justify-center mt-4 disabled:opacity-60"
            >
              {loadingOlder ? 'Loading…' : 'View older'}
            </button>
          ) : null}
        </section>
      )}
    </div>
  );
}

function LearnerBalanceHero() {
  const sessionMinutes = useAppStore((s) => s.sessionMinutes);
  return (
    <div className="card p-5 sm:p-6 has-grain">
      <div className="text-eyebrow mb-2">Balance</div>
      <div className="flex items-baseline gap-3">
        <span className="font-mono tabular-nums text-5xl font-semibold leading-none text-ink-900 dark:text-ink-100">
          {sessionMinutes}
        </span>
        <span className="text-[0.95rem] text-ink-500 dark:text-ink-400">minutes on hand</span>
      </div>
    </div>
  );
}

function LearnerHeadline({ minutesUsed, topUpsDollars, topUpCount }: {
  minutesUsed: number; topUpsDollars: number; topUpCount: number;
}) {
  return (
    <div className="space-y-2">
      <div className="text-eyebrow">This month</div>
      <div className="flex justify-between text-[0.95rem]">
        <span className="text-ink-600 dark:text-ink-400">Top-ups</span>
        <span className="font-mono tabular-nums">
          {money(topUpsDollars)} <span className="text-ink-500 dark:text-ink-400">({topUpCount})</span>
        </span>
      </div>
      {/* No dollar equivalence: what a minute cost depends on the package that
          funded it, which the ledger does not record. */}
      <div className="flex justify-between text-[0.95rem]">
        <span className="text-ink-600 dark:text-ink-400">Minutes used</span>
        <span className="font-mono tabular-nums">{minutesUsed}</span>
      </div>
    </div>
  );
}

function GuideHeadline({ dollars, minutes }: { dollars: number; minutes: number }) {
  const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  const monthLabel = monthStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return (
    <div className="card p-5 sm:p-6 has-grain">
      <div className="text-eyebrow mb-2">This month</div>
      <div className="flex items-baseline gap-3">
        <span className="font-mono tabular-nums text-5xl font-semibold leading-none text-ink-900 dark:text-ink-100">
          {money(dollars)}
        </span>
        <span className="text-[0.95rem] text-ink-500 dark:text-ink-400">earned</span>
      </div>
      <div className="mt-2 text-[0.9rem] text-ink-600 dark:text-ink-400">
        {minutes} minutes · since {monthLabel}
      </div>
    </div>
  );
}

/** Strips the "admin: " prefix admin_adjust_balance and the reversal RPCs put
 *  in front of the operator's reason. */
function reasonOf(note: string | null): string | null {
  return note ? note.replace(/^admin:\s*/, '') : null;
}

function labelFor(entry: LedgerEntryRow): string {
  switch (entry.kind) {
    case 'call':     return entry.peerName ? `Call with ${entry.peerName}` : 'Call';
    case 'charge':   return 'Top-up';
    case 'adjust':   return reasonOf(entry.note) ? `Adjustment — ${reasonOf(entry.note)}` : 'Adjustment';
    case 'failed':   return reasonOf(entry.note) ? `Payment failed — ${reasonOf(entry.note)}` : 'Payment failed';
    case 'dispute':  return 'Payment disputed';
    case 'reversal': return reasonOf(entry.note) ? `Refund — ${reasonOf(entry.note)}` : 'Refund';
    // Distinct wording from 'reversal' above (which reverses a specific call):
    // nothing writes 'refund' rows yet, but billing_events_kind_check permits
    // the kind and the RPC's catch-all branch passes it straight through, so
    // this screen must not render it blank.
    case 'refund':   return reasonOf(entry.note) ? `Payment refunded — ${reasonOf(entry.note)}` : 'Payment refunded';
    // The switch above is exhaustive over LedgerEntryKind today, so this arm
    // is unreachable by the type system — but the type is a lie at the wire
    // boundary: client.ts casts with `r.kind as LedgerEntryKind` without
    // validating, and the RPC's catch-all branch passes any kind straight
    // through. Widen billing_events_kind_check in a future migration and,
    // without this, the row would render with a blank label.
    default:         return 'Activity';
  }
}

function EntryRow({ entry, role }: { entry: LedgerEntryRow; role: UserRole }) {
  const dateStr = new Date(entry.occurredAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  // A guide shows the call's duration (their own rows carry minutes_delta 0);
  // a learner shows their signed balance movement, so a refund reads "+31m"
  // against the call's "-31m".
  const minutesLabel = role === 'guide'
    ? (entry.sessionMinutes != null ? `${entry.sessionMinutes}m` : null)
    : (entry.minutesDelta !== 0 ? `${entry.minutesDelta > 0 ? '+' : ''}${entry.minutesDelta}m` : null);
  return (
    <li data-testid="timeline-row" className="flex items-center justify-between py-3 gap-4 text-[0.95rem]">
      <div className="min-w-0">
        <div className="text-ink-500 dark:text-ink-400 text-[0.8rem]">{dateStr}</div>
        <div className="text-ink-900 dark:text-ink-100 truncate">{labelFor(entry)}</div>
      </div>
      <div className="text-right shrink-0">
        <div className="font-mono tabular-nums text-ink-900 dark:text-ink-100">
          {minutesLabel ? <span>{minutesLabel}</span> : null}
          {entry.dollars != null ? (
            <span className="text-ink-500 dark:text-ink-400">{minutesLabel ? ' ' : ''}{money(entry.dollars)}</span>
          ) : null}
        </div>
        {entry.balanceAfter != null ? (
          <div className="text-ink-500 dark:text-ink-400 text-[0.8rem]">{entry.balanceAfter} min left</div>
        ) : null}
      </div>
    </li>
  );
}
